import React, { useMemo, useRef } from "react";

import { useTranslation } from "react-i18next";
import { Dropdown } from "primereact/dropdown";

import { FilteringType, IFilterSelectInputProps } from "../interfaces/filter-interfaces";
import { FilterTypePopup } from "../components/pop-ups/filter-type-popup";
import SuperscriptDisplay from "../../superscript/superscript-display";
import { memoFilter } from "./memoFilter";

// table header filter component, you send it in the column as filterBody prop
// eg. (filterBody: (inputData: IPrimeFilterData) => <MemoFilterSelectInput inputData={inputData} />)
export const MemoFilterSelectInput = memoFilter((props: IFilterSelectInputProps) => {
    const { column, isFilterDisabled } = props.inputData;

    if (isFilterDisabled) return <DisabledFilterSelectInput field={column.field} />;
    else return <FilterSelectInput {...props} />;
});

export const FilterSelectInput = ({
    inputData,
    optionFilter = false,
    placeholder,
    clearable = true,
    superscript = false,
    searchType = "__exact",
}: IFilterSelectInputProps) => {
    const { t } = useTranslation();
    const { column, onFilterChange, filter, filterChoices, tableRef } = inputData;

    const filterType: FilteringType = filter?.filterType || searchType;

    const inputRef = useRef<HTMLDivElement>(null);

    const _filterChoices = superscript
        ? useMemo(() => {
              return filterChoices?.map((o, i) => ({
                  label: (
                      <div>
                          <SuperscriptDisplay key={`opt-${i}`} value={o.label} />
                      </div>
                  ),
                  stringValue: o.label.replace(/[↑↓→←]/gi, ""),
                  value: o.value,
              }));
          }, [filterChoices])
        : filterChoices;

    const handleFilterTypeChange = (newFilterType) => {
        if (
            (typeof filter?.value == "number" && filter?.value == undefined) ||
            (typeof filter?.value == "string" && filter?.value.trim() == "")
        )
            return;

        onFilterChange({ value: filter?.value, field: column.field, filterField: column.filterField }, newFilterType);
    };

    return (
        <div key={`prime-filter-input-${column.field}-container`} className="table-filter" ref={inputRef}>
            <Dropdown
                key={`prime-filter-input-${column.field}`}
                className="prime-table-filter-input-font filter-select"
                name={column.filterField}
                value={filter?.value}
                options={_filterChoices || []}
                onChange={(e) =>
                    onFilterChange({ filterField: column.filterField, field: column.field, value: e.target.value }, filterType)
                }
                optionLabel="label"
                optionValue="value"
                placeholder={placeholder ? t(placeholder) : `${t("Filter")} ${t(column.header)}`}
                filter={optionFilter}
                showClear={clearable}
                emptyMessage={t("No records found")}
            />
            <FilterTypePopup
                t={t}
                type={"select"}
                tableRef={tableRef || inputRef}
                inputRef={inputRef}
                filterType={filterType}
                handleFilterTypeChange={handleFilterTypeChange}
            />
        </div>
    );
};

interface IDisabledFilterSelectInput {
    field: string;
}

export const DisabledFilterSelectInput = ({ field }: IDisabledFilterSelectInput) => {
    const { t } = useTranslation();

    return (
        <div key={`prime-filter-input-${field}-container-disabled`} className="filter-select-input-container">
            <Dropdown
                key={`prime-filter-input-${field}-disabled`}
                className="table-filter prime-table-filter-input-font"
                placeholder={t("Advanced filter...")}
                disabled
            />
        </div>
    );
};
